import { Link } from 'react-router-dom'

const STEPS = [
  {
    num: '01',
    title: 'Créer et compléter votre compte entreprise',
    icon: '🏢',
    content: [
      'Renseignez votre <strong>SIRET</strong> et votre adresse dans les paramètres. Ces informations apparaissent dans vos documents officiels et dans la page Mentions légales.',
      'Uploadez votre <strong>logo</strong> et complétez la description de votre activité. Un profil complet inspire confiance aux prestataires.',
      'Si vous organisez des événements de sécurité privée, activez le badge <strong>CNAPS</strong> dans vos paramètres pour attirer les agents certifiés.',
    ],
  },
  {
    num: '02',
    title: 'Publier une mission',
    icon: '📋',
    content: [
      'Cliquez sur <strong>Nouvelle mission</strong>. Renseignez le titre, la description, le type (Événement, Formation, Sécurité ADS/SSIAP, Autre), la date, la durée et le montant en euros.',
      'L\'adresse est géocodée automatiquement. Définissez un <strong>rayon de recherche</strong> (en km) pour cibler les prestataires à proximité.',
      'Vous pouvez exiger une <strong>spécialité précise</strong> (ex. : SSIAP 1, Agent ADS, Formateur SST). Seuls les prestataires qualifiés verront la mission.',
    ],
  },
  {
    num: '03',
    title: 'Les prestataires reçoivent une notification',
    icon: '🔔',
    content: [
      'À la publication, tous les prestataires correspondants dans le rayon reçoivent une <strong>notification push</strong> sur l\'application mobile Velink.',
      'La notification indique le montant, la date et la ville — sans dévoiler l\'adresse exacte pour préserver votre confidentialité.',
      'Le premier prestataire à accepter verrouille la mission. La plateforme vous notifie immédiatement par email.',
    ],
  },
  {
    num: '04',
    title: 'Gérer les candidatures et choisir le prestataire',
    icon: '👥',
    content: [
      'Dans <strong>Mes missions</strong>, consultez le profil, les qualifications et la note des candidats.',
      'Pour les missions de type <strong>formation</strong>, vous pouvez publier une demande de formation distincte (onglet Demandes formation) pour recevoir des propositions tarifaires de centres de formation.',
      'Une fois le prestataire sélectionné, le statut passe en <strong>Assignée</strong> et le paiement est pré-autorisé.',
    ],
  },
  {
    num: '05',
    title: 'Sécuriser le paiement',
    icon: '🔒',
    content: [
      'Le paiement est mis en <strong>séquestre (escrow)</strong> dès la publication : la somme est réservée sur votre compte Stripe ou PayPal sans être débitée.',
      'Après la mission, confirmez la réalisation. Le montant est capturé et reversé au prestataire sous 2–5 jours ouvrés.',
      'En cas de litige, contactez Velink avant de confirmer. Le montant reste gelé jusqu\'à résolution.',
    ],
  },
  {
    num: '06',
    title: 'Suivre vos missions et analyser l\'activité',
    icon: '📊',
    content: [
      'L\'onglet <strong>Analytics</strong> affiche vos dépenses par mois, le nombre de missions par statut et vos prestataires les plus fréquents.',
      'Vous pouvez filtrer par période et exporter les données pour votre comptabilité.',
      'Les missions complétées sont archivées et consultables à tout moment dans l\'historique.',
    ],
  },
]

const COMMISSION = [
  { label: 'Première mission', value: '0 %', highlight: true, desc: 'Testez la plateforme sans frais.' },
  { label: 'À partir de la 2e mission', value: '10 %', highlight: false, desc: 'Commission sur le montant HT de chaque mission.' },
  { label: 'Côté prestataire', value: '0 %', highlight: true, desc: 'Les prestataires ne paient rien — jamais.' },
]

const FAQ = [
  { q: 'Puis-je annuler une mission après publication ?', a: 'Oui, tant que le statut est "Ouverte". Si un prestataire a déjà accepté, vous pouvez annuler mais le pré-paiement peut être dû selon les conditions. Contactez le support pour les cas particuliers.' },
  { q: 'Que faire si le prestataire ne se présente pas ?', a: 'Ne confirmez pas la mission. Ouvrez un litige via le support (contact@velink.fr). Le paiement escrow sera maintenu et le problème traité sous 48 h.' },
  { q: 'Comment fonctionne la demande de formation ?', a: 'Vous publiez une demande avec votre sujet, votre budget et votre date souhaitée. Les centres de formation répondent avec leurs propositions. Vous choisissez et validez la proposition qui vous convient.' },
  { q: 'Puis-je travailler avec le même prestataire régulièrement ?', a: 'Oui. Vous pouvez voir ses coordonnées après la première mission. La commission de 10 % s\'applique à chaque mission à partir de la 2e.' },
  { q: 'Velink est-il compatible avec mon logiciel RH ?', a: 'Pas encore nativement. Vous pouvez exporter vos données depuis l\'onglet Analytics au format CSV.' },
]

export default function GuideEntreprisePage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-medium mb-4">
          Guide entreprise — De la mission au paiement
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">Comment utiliser Velink</h1>
        <p className="text-text-secondary text-sm leading-relaxed max-w-2xl">
          Velink vous connecte en quelques minutes avec des prestataires indépendants qualifiés pour vos événements,
          formations et prestations de sécurité. Ce guide couvre l'ensemble du flux, de la publication au virement.
        </p>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {STEPS.map((step) => (
          <div key={step.num} className="glass-card rounded-xl overflow-hidden">
            <div className="flex items-center gap-4 p-5">
              <div className="w-10 h-10 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{step.icon}</span>
              </div>
              <div className="flex-1">
                <div className="mb-1">
                  <span className="text-xs font-mono text-text-muted">Étape {step.num}</span>
                </div>
                <h2 className="text-text-primary font-semibold">{step.title}</h2>
              </div>
            </div>
            <div className="px-5 pb-5 border-t border-white/[0.06] pt-4">
              <ul className="space-y-2">
                {step.content.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-text-secondary text-sm leading-relaxed">
                    <span className="text-accent mt-0.5 flex-shrink-0">→</span>
                    <span dangerouslySetInnerHTML={{ __html: line }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Commission */}
      <div>
        <h2 className="text-text-primary font-semibold mb-4 flex items-center gap-2">
          <span>💶</span> Modèle de commission
        </h2>
        <div className="grid grid-cols-3 gap-3">
          {COMMISSION.map((c) => (
            <div key={c.label} className={`glass-card p-4 rounded-xl text-center ${c.highlight ? 'border-success/30' : ''}`}>
              <p className={`text-2xl font-bold mb-1 ${c.highlight ? 'text-success' : 'text-text-primary'}`}>{c.value}</p>
              <p className="text-text-primary text-xs font-semibold mb-1">{c.label}</p>
              <p className="text-text-muted text-xs">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="glass-card p-5 rounded-xl">
        <h2 className="text-text-primary font-semibold mb-4">Actions rapides</h2>
        <div className="grid grid-cols-2 gap-3">
          <Link to="/missions/nouvelle" className="glass-btn-accent px-4 py-3 text-sm rounded-xl text-center block">
            + Publier une mission
          </Link>
          <Link to="/formations" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Demander une formation
          </Link>
          <Link to="/analytics" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Voir mes analytics
          </Link>
          <Link to="/parametres" className="glass-btn px-4 py-3 text-sm rounded-xl text-center block">
            Compléter mon profil
          </Link>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-text-primary font-semibold mb-4">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div key={i} className="glass-card p-5 rounded-xl">
              <p className="text-text-primary font-medium text-sm mb-2">{item.q}</p>
              <p className="text-text-secondary text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="glass-card p-5 rounded-xl flex items-start gap-4">
        <span className="text-2xl">✉️</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-1">Besoin d'aide ?</p>
          <p className="text-text-secondary text-sm">
            Contactez notre équipe à{' '}
            <a href="mailto:contact@velink.fr" className="text-accent hover:underline">contact@velink.fr</a>.
            Nous répondons sous 24 h (jours ouvrés).
          </p>
        </div>
      </div>
    </div>
  )
}
